import { useMemo } from 'react';

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@/shared/ui';

import { GX_MINI_TABLE_HIGHLIGHT, GX_TILE_LIGHT } from '@/config/colors';

import { DateTextVariants, useDateText, useUserDetails } from '@/features/_shared/user';
import { FactsheetDialogButton } from '@/features/factsheet';
import { getMiniTableSymbols } from '@/features/markets/components/MiniTablesContainer/lib/getMiniTableSymbols';
import type { MiniTableBodyRow, MiniTableType } from '@/features/markets/types';
import { useSelectionFunctions } from '@/features/symbol-selector';
import { isValidDateString } from '@/shared/lib/date/isValidDateString';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import CustomCheckbox from '@/shared/ui/primitives/Checkbox';

import type { Sx } from '@/shared/types/uiTypes';

const headerBoxSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  pl: '27px',
  pr: '16px',
  height: '26px'
};

const titleSx: Sx = {
  fontSize: '14px',
  fontWeight: 700,
  flex: 1,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  overflow: 'hidden'
};

const checkboxSx: Sx = { mb: '3px' };

const tableSx: Sx = { borderCollapse: 'collapse' };

const tableHeadRowSx: Sx = { borderBottom: '1px solid', borderColor: 'white' };

const headerCellSx: Sx = { pl: '27px', pr: '16px', backgroundColor: 'gx.tileLight' };

const tableBodySx: Sx = {
  '& .markets-table-row:nth-of-type(even)': { backgroundColor: 'gx.tileLight' }
};

const firstCellSx: Sx = { pl: '27px', pr: '16px', width: '40%' };
const otherCellSx: Sx = { pl: 0, pr: '16px', width: '30%' };

function roundHalfUp(num: number, decimals = 0) {
  const factor = Math.pow(10, decimals);
  return (Math.sign(num) * Math.round(Math.abs(num) * factor)) / factor;
}

function formatNumberInBraces(value: string) {
  return value.replaceAll(/\{([^{}]*)}/g, (_, content) => {
    const result = content.trim();
    if (!result) return '';
    if (Number.isNaN(Number(result))) return result;
    try {
      const decimalsToRound = 2;
      return formatNumberWithPrecision(
        roundHalfUp(Number(result), decimalsToRound).toFixed(decimalsToRound)
      );
    } catch {
      return result;
    }
  });
}

const MiniTable = ({ table }: { table: MiniTableType }) => {
  const { formatDateStringInBraces } = useDateText();

  const { addSymbolsToSelection, areAllSymbolsSelected, removeSymbolsFromSelection, isSpotLocked } =
    useSelectionFunctions();

  const symbols = useMemo(() => getMiniTableSymbols(table), [table]);

  const isSymbolsSelected = useMemo(() => {
    return areAllSymbolsSelected(symbols);
  }, [symbols, areAllSymbolsSelected]);

  const { thead, tbody } = useMemo(() => {
    if ('symbol' in table.rows[0]) {
      return { thead: null, tbody: table.rows as MiniTableBodyRow[] };
    }
    return {
      thead: table.rows[0],
      tbody: table.rows.slice(1) as MiniTableBodyRow[]
    };
  }, [table]);

  const { isUserExpired } = useUserDetails();
  const factsheetCodes = useMemo(() => {
    return symbols.map(({ Code }) => Code).join(',');
  }, [symbols]);
  return (
    <Box>
      <Box sx={headerBoxSx}>
        <CustomCheckbox
          checked={isSymbolsSelected}
          disabled={isUserExpired}
          modeLocked={isSpotLocked}
          onLockedClick={() => {
            addSymbolsToSelection(symbols);
          }}
          sx={checkboxSx}
          onChange={() => {
            if (isSymbolsSelected) {
              removeSymbolsFromSelection(symbols);
            } else {
              addSymbolsToSelection(symbols);
            }
          }}
        />
        <Typography sx={titleSx}>{table.name}</Typography>

        <FactsheetDialogButton code={factsheetCodes} />
      </Box>
      <Table
        stickyHeader
        aria-label='commodities table'
        size='small'
        sx={tableSx}
      >
        <TableHead>
          <TableRow sx={tableHeadRowSx}>
            {thead?.cells.map((cell, i) => (
              <TableCell
                key={`thead-cell-${cell}-${i}`}
                align={i > 0 ? 'right' : 'left'}
                sx={headerCellSx}
              >
                {cell}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={tableBodySx}>
          {tbody.map((row, rowIndex) => {
            const isHighlighted = row.layout?.highlight === 'true';
            let backgroundColor = 'transparent';

            if (isHighlighted) {
              backgroundColor = GX_MINI_TABLE_HIGHLIGHT;
            } else if (rowIndex % 2 === 0) {
              backgroundColor = GX_TILE_LIGHT;
            }
            const rowKey = row.cells[0] || `row-${rowIndex}`;

            return (
              <TableRow
                key={`body-${rowKey}`}
                className='markets-table-row'
                style={{ backgroundColor }}
              >
                {row.cells.map((cell, cellIndex) => {
                  const isFirstCell = cellIndex === 0;
                  const trimmedCell = cell.slice(1, -1);
                  const isDate =
                    Number.isNaN(Number(trimmedCell)) && isValidDateString(trimmedCell);
                  const colSpan = cell === '' && row.cells.length === 1 ? 3 : 1;

                  let content = '';
                  if (cell !== '') {
                    content = isDate
                      ? formatDateStringInBraces(cell, DateTextVariants.MONTH_YEAR)
                      : formatNumberInBraces(cell);
                  }

                  return (
                    <TableCell
                      key={`${rowIndex}-${cellIndex}`}
                      align={isFirstCell ? 'left' : 'right'}
                      colSpan={colSpan}
                      sx={isFirstCell ? firstCellSx : otherCellSx}
                    >
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
export default MiniTable;
